/**
 * Natural Language Parser
 * Converts tokens and intent into structured AST patterns
 */

import { diagnostics as engineDiagnostics } from '@crown/dsl-compiler';
import type {
  NLAbsoluteDateComparisonPattern,
  NLBooleanOp,
  NLBooleanPattern,
  NLCombinedPattern,
  NLComparator,
  NLComparisonPattern,
  NLConditionalPattern,
  NLDateArithmeticPattern,
  NLDateComparisonPattern,
  NLFieldEqualityPattern,
  NLLengthPattern,
  NLParsedPattern,
  NLParseResult,
  NLPattern,
  NLPresencePattern,
  NLRangePattern,
  NLTextMatchPattern,
  NLToken,
} from './ast.js';
import {
  getBestIntent,
  getPossibleIntents,
  type IntentMatch,
} from './intent.js';
import {
  extractDateLiterals,
  extractFields,
  extractNumbers,
  findConnectors,
  hasComparator,
  hasKeyword,
} from './lexer.js';

/**
 * Parse natural language into structured pattern
 */
export function parse(tokens: NLToken[], input: string): NLParseResult {
  const diagnostics: Array<{
    message: string;
    severity: 'error' | 'warning' | 'info';
    suggestion?: string;
  }> = [];

  const modifiers = {
    dateOnly: /\(date only\)/i.test(input),
    ignoreCase: /\(ignore case\)/i.test(input),
  };

  const cleanInput = input.replace(/\s*\([^)]+\)/g, '').trim();

  const bestIntent = getBestIntent(tokens, cleanInput);

  if (bestIntent.confidence < 0.3) {
    return {
      pattern: null,
      expression: null,
      confidence: 0,
      diagnostics: [
        {
          message: 'Could not understand the natural language input',
          severity: 'error',
          suggestion:
            'Try using patterns like "age is at least 18" or "field is checked"',
        },
      ],
      alternatives: [],
    };
  }

  let pattern: NLParsedPattern | null = null;

  try {
    switch (bestIntent.intent) {
      case 'comparison':
        pattern = parseComparison(tokens, cleanInput, bestIntent);
        break;
      case 'presence':
        pattern = parsePresence(tokens, cleanInput, bestIntent);
        break;
      case 'range':
        pattern = parseRange(tokens, cleanInput, bestIntent);
        break;
      case 'text-match': {
        const textMatch = parseTextMatch(tokens, cleanInput, bestIntent);
        if (modifiers.ignoreCase && textMatch.operation === 'matches') {
          textMatch.caseSensitive = false;
        }
        pattern = textMatch;
        break;
      }
      case 'length':
        pattern = parseLength(tokens, cleanInput, bestIntent);
        break;
      case 'boolean':
        pattern = parseBoolean(tokens, cleanInput, bestIntent);
        break;
      case 'date-comparison': {
        const dateComparison = parseDateComparison(
          tokens,
          cleanInput,
          bestIntent,
        );
        if (modifiers.dateOnly) {
          dateComparison.dateOnly = true;
        }
        pattern = dateComparison;
        break;
      }
      case 'date-arithmetic':
        pattern = parseDateArithmetic(
          tokens,
          cleanInput,
          bestIntent,
          modifiers,
        );
        break;
      case 'absolute-date-comparison':
        pattern = parseAbsoluteDateComparison(tokens, cleanInput, bestIntent);
        break;
      case 'conditional':
        pattern = parseConditional(tokens, cleanInput, bestIntent);
        break;
      case 'field-equality': {
        const fieldEquality = parseFieldEquality(
          tokens,
          cleanInput,
          bestIntent,
        );
        if (modifiers.ignoreCase) {
          fieldEquality.caseSensitive = false;
        }
        pattern = fieldEquality;
        break;
      }
      case 'combined':
        pattern = parseCombined(tokens, cleanInput, bestIntent);
        break;
      default:
        throw new Error(`Unsupported intent: ${bestIntent.intent}`);
    }
  } catch (error) {
    diagnostics.push({
      message: `Parse error: ${error instanceof Error ? error.message : String(error)}`,
      severity: 'error',
    });
  }

  if (!pattern) {
    return {
      pattern: null,
      expression: null,
      confidence: 0,
      diagnostics,
      alternatives: [],
    };
  }

  let expression: string | null = generateExpression(pattern);
  /*
    Check what was generated with the engine that will run it.

    This parsed the generated expression with a second parser written in
    TypeScript, so "is this valid?" was answered by something other than the
    thing that decides. Anything the two disagreed about was either reported
    as invalid and thrown away here, or accepted here and refused later by the
    engine - and the second is the worse one, because the phrase looked
    understood.
  */
  const generatedErrors = engineDiagnostics(expression).filter(
    (diagnostic) => diagnostic.severity === 'error',
  );
  if (generatedErrors.length > 0) {
    diagnostics.push({
      message: `Generated an invalid expression: ${generatedErrors[0]?.message ?? 'unknown error'}`,
      severity: 'error',
      suggestion:
        'Use parseNaturalLanguageDetailed() to inspect the recognized intent.',
    });
    expression = null;
  }

  const alternatives = getPossibleIntents(tokens, input, 0.4)
    .slice(1, 4)
    .map((intent) => {
      try {
        const altPattern = parseByIntent(tokens, input, intent);
        return altPattern
          ? {
              pattern: altPattern,
              expression: generateExpression(altPattern),
              confidence: intent.confidence,
              description: describeIntent(intent),
            }
          : null;
      } catch {
        return null;
      }
    })
    .filter((a): a is NonNullable<typeof a> => a !== null);

  return {
    pattern,
    expression,
    confidence: bestIntent.confidence,
    diagnostics,
    alternatives,
  };
}

/**
 * Parse by specific intent (helper for alternatives)
 */
function parseByIntent(
  tokens: NLToken[],
  input: string,
  intent: IntentMatch,
): NLParsedPattern | null {
  switch (intent.intent) {
    case 'comparison':
      return parseComparison(tokens, input, intent);
    case 'presence':
      return parsePresence(tokens, input, intent);
    case 'range':
      return parseRange(tokens, input, intent);
    case 'text-match':
      return parseTextMatch(tokens, input, intent);
    case 'length':
      return parseLength(tokens, input, intent);
    case 'boolean':
      return parseBoolean(tokens, input, intent);
    case 'date-comparison':
      return parseDateComparison(tokens, input, intent);
    case 'date-arithmetic':
      return parseDateArithmetic(tokens, input, intent);
    case 'absolute-date-comparison':
      return parseAbsoluteDateComparison(tokens, input, intent);
    case 'conditional':
      return parseConditional(tokens, input, intent);
    case 'field-equality':
      return parseFieldEquality(tokens, input, intent);
    case 'combined':
      return parseCombined(tokens, input, intent);
    default:
      return null;
  }
}

/**
 * Parse comparison pattern
 */
function parseComparison(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLComparisonPattern {
  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  const [field] = fields;
  const [value] = numbers;
  if (field === undefined) throw new Error('No field found');
  if (value === undefined) throw new Error('No value found');

  let comparator: NLComparator = 'at-least';

  const tokenStr = tokens
    .map((t) => t.value)
    .join(' ')
    .toLowerCase();
  const tokenWords = tokenStr.split(/\s+/);
  const symbolicOperator = tokens.find(
    (token) => token.type === 'operator',
  )?.value;

  if (
    symbolicOperator === '>' ||
    hasComparator(tokens, 'greater than') ||
    hasComparator(tokens, 'more than') ||
    tokenWords.includes('over') ||
    hasKeyword(tokens, 'above')
  ) {
    comparator = 'greater-than';
  } else if (
    symbolicOperator === '>=' ||
    hasComparator(tokens, 'at least') ||
    hasComparator(tokens, 'is at least') ||
    hasComparator(tokens, 'no less than')
  ) {
    comparator = 'at-least';
  } else if (
    symbolicOperator === '<' ||
    hasComparator(tokens, 'less than') ||
    hasKeyword(tokens, 'below') ||
    hasKeyword(tokens, 'under')
  ) {
    comparator = 'less-than';
  } else if (
    symbolicOperator === '<=' ||
    hasComparator(tokens, 'at most') ||
    hasComparator(tokens, 'no more than') ||
    hasComparator(tokens, 'must not exceed')
  ) {
    comparator = 'at-most';
  } else if (
    symbolicOperator === '!=' ||
    hasComparator(tokens, 'not equal to')
  ) {
    comparator = 'not-equals';
  } else if (
    symbolicOperator === '==' ||
    symbolicOperator === '=' ||
    hasKeyword(tokens, 'exactly') ||
    hasKeyword(tokens, 'equals') ||
    hasKeyword(tokens, 'equal')
  ) {
    comparator = 'equals';
  }

  return {
    intent: 'comparison',
    confidence: intent.confidence,
    tokens,
    field,
    comparator,
    value,
  };
}

/**
 * Parse presence pattern
 */
function parsePresence(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLPresencePattern {
  const fields = extractFields(tokens);
  const [field] = fields;
  if (field === undefined) throw new Error('No field found');
  const negated =
    hasKeyword(tokens, 'empty') ||
    hasKeyword(tokens, 'blank') ||
    hasKeyword(tokens, 'missing') ||
    hasKeyword(tokens, 'not');

  return {
    intent: 'presence',
    confidence: intent.confidence,
    tokens,
    field,
    negated,
  };
}

/**
 * Parse range pattern
 */
function parseRange(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLRangePattern {
  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  const [field] = fields;
  if (field === undefined) throw new Error('No field found');
  const negated = hasKeyword(tokens, 'outside');

  const [minNumber, maxNumber] = numbers;
  const [, minField, maxField] = fields;
  if (minNumber !== undefined && maxNumber !== undefined) {
    return {
      intent: 'range',
      confidence: intent.confidence,
      tokens,
      field,
      min: minNumber,
      max: maxNumber,
      inclusive: true,
      negated,
    };
  } else if (minField !== undefined && maxField !== undefined) {
    return {
      intent: 'range',
      confidence: intent.confidence,
      tokens,
      field,
      min: minField,
      max: maxField,
      inclusive: true,
      negated,
    };
  }

  throw new Error('Range pattern requires min and max values');
}

/**
 * Parse text match pattern
 */
function parseTextMatch(
  tokens: NLToken[],
  input: string,
  intent: IntentMatch,
): NLTextMatchPattern {
  const fields = extractFields(tokens);
  const [field] = fields;
  if (field === undefined) throw new Error('No field found');
  const strings = tokens.filter((t) => t.type === 'string');
  const value =
    strings[0]?.normalized ?? strings[0]?.value.replace(/^["']|["']$/g, '');

  let operation: NLTextMatchPattern['operation'] = 'contains';

  if (hasKeyword(tokens, 'contains') || hasKeyword(tokens, 'contain')) {
    operation = 'contains';
  } else if (hasKeyword(tokens, 'starts')) {
    operation = 'starts-with';
  } else if (hasKeyword(tokens, 'ends')) {
    operation = 'ends-with';
  } else if (
    hasKeyword(tokens, 'matches') ||
    hasKeyword(tokens, 'equals') ||
    hasKeyword(tokens, 'equal')
  ) {
    operation = 'matches';
  } else if (hasKeyword(tokens, 'has') && hasKeyword(tokens, 'text')) {
    operation = 'has-text';
  }

  const caseSensitive =
    !hasKeyword(tokens, 'ignore') && !hasKeyword(tokens, 'case');

  return {
    intent: 'text-match',
    confidence: intent.confidence,
    tokens,
    field,
    operation,
    value,
    caseSensitive,
    negated: hasKeyword(tokens, 'not') || /\bdoes\s+not\b/i.test(input),
  };
}

function parseLength(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLLengthPattern {
  const field = extractFields(tokens)[0];
  const length = extractNumbers(tokens)[0];
  if (!field || length === undefined)
    throw new Error('Length pattern requires a field and number');
  let comparator: NLLengthPattern['comparator'] = 'equals';
  if (
    hasComparator(tokens, 'at least') ||
    hasComparator(tokens, 'no less than')
  )
    comparator = 'at-least';
  else if (
    hasComparator(tokens, 'greater than') ||
    hasKeyword(tokens, 'above') ||
    hasKeyword(tokens, 'over')
  )
    comparator = 'greater-than';
  else if (
    hasComparator(tokens, 'at most') ||
    hasComparator(tokens, 'no more than')
  )
    comparator = 'at-most';
  else if (
    hasComparator(tokens, 'less than') ||
    hasKeyword(tokens, 'below') ||
    hasKeyword(tokens, 'under')
  )
    comparator = 'less-than';
  return {
    intent: 'length',
    confidence: intent.confidence,
    tokens,
    field,
    comparator,
    length,
  };
}

/**
 * Parse boolean pattern
 */
function parseBoolean(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLBooleanPattern {
  const fields = extractFields(tokens);
  const [field] = fields;
  if (field === undefined) throw new Error('No field found');
  const expected =
    !hasKeyword(tokens, 'not') && !hasKeyword(tokens, 'unchecked');

  return {
    intent: 'boolean',
    confidence: intent.confidence,
    tokens,
    field,
    expected,
  };
}

/**
 * Parse date comparison pattern
 */
function parseDateComparison(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLDateComparisonPattern {
  const fields = extractFields(tokens);
  const [field1, field2] = fields;
  if (field1 === undefined || field2 === undefined)
    throw new Error('Date comparison requires two fields');

  let comparator: NLDateComparisonPattern['comparator'] = 'after';

  if (hasKeyword(tokens, 'after')) {
    comparator = 'after';
  } else if (hasKeyword(tokens, 'before')) {
    comparator = 'before';
  } else if (
    hasComparator(tokens, 'on or after') ||
    hasComparator(tokens, 'same or after') ||
    hasComparator(tokens, 'no earlier than')
  ) {
    comparator = 'same-or-after';
  } else if (
    hasComparator(tokens, 'on or before') ||
    hasComparator(tokens, 'same or before') ||
    hasComparator(tokens, 'no later than')
  ) {
    comparator = 'same-or-before';
  }

  const dateOnly = hasKeyword(tokens, 'date') && hasKeyword(tokens, 'only');

  return {
    intent: 'date-comparison',
    confidence: intent.confidence,
    tokens,
    field1,
    field2,
    comparator,
    dateOnly,
  };
}

/**
 * Parse date arithmetic pattern
 */
function parseDateArithmetic(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
  _modifiers?: { dateOnly: boolean; ignoreCase: boolean },
): NLDateArithmeticPattern {
  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  const [amount] = numbers;
  if (amount === undefined) throw new Error('No amount found');

  let unit: 'days' | 'hours' | 'minutes' = 'days';
  if (hasKeyword(tokens, 'hour') || hasKeyword(tokens, 'hours')) {
    unit = 'hours';
  } else if (hasKeyword(tokens, 'minute') || hasKeyword(tokens, 'minutes')) {
    unit = 'minutes';
  }

  let comparator: 'at-least' | 'at-most' | 'exactly' | 'within' = 'at-least';

  const tokenStr = tokens
    .map((t) => t.value)
    .join(' ')
    .toLowerCase();

  if (hasKeyword(tokens, 'within') || tokenStr.includes('within')) {
    comparator = 'within';
  } else if (hasKeyword(tokens, 'exactly') || tokenStr.includes('exactly')) {
    comparator = 'exactly';
  } else if (
    tokenStr.includes('not more than') ||
    tokenStr.includes('no more than') ||
    tokenStr.includes('at most')
  ) {
    comparator = 'at-most';
  } else if (tokenStr.includes('at least')) {
    comparator = 'at-least';
  }

  const hasAgo = hasKeyword(tokens, 'ago');
  const hasLast = hasKeyword(tokens, 'last');
  const isRelativeToNow = hasAgo || (hasKeyword(tokens, 'within') && hasLast);

  let field1: string;
  let field2: string;
  let direction: 'after' | 'before' | 'of';

  const [firstField, secondField] = fields;
  if (isRelativeToNow) {
    if (firstField === undefined) throw new Error('No field found');
    field1 = 'now()';
    field2 = firstField;
    direction = 'after';
  } else {
    if (firstField === undefined || secondField === undefined)
      throw new Error('Date arithmetic requires two fields');

    direction = hasKeyword(tokens, 'before')
      ? 'before'
      : hasKeyword(tokens, 'of')
        ? 'of'
        : 'after';

    if (direction === 'before') {
      field1 = secondField;
      field2 = firstField;
    } else {
      field1 = firstField;
      field2 = secondField;
    }
  }

  return {
    intent: 'date-arithmetic',
    confidence: intent.confidence,
    tokens,
    field1,
    field2,
    unit,
    amount,
    comparator,
    direction,
  };
}

/**
 * Parse absolute date comparison pattern
 * "root.something is greater than or equal to the 5th of April 2022"
 * "admission is after April 5, 2022"
 */
function parseAbsoluteDateComparison(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLAbsoluteDateComparisonPattern {
  const fields = extractFields(tokens);
  const [field] = fields;
  if (field === undefined)
    throw new Error('No field found in absolute date comparison');

  const dateLiterals = extractDateLiterals(tokens);
  const [firstDateLiteral] = dateLiterals;
  if (firstDateLiteral === undefined) throw new Error('No date literal found');

  const dateISO = firstDateLiteral.isoDate;

  let comparator: NLAbsoluteDateComparisonPattern['comparator'] =
    'same-or-after';

  // Check longest/most-specific comparators first
  if (
    hasComparator(tokens, 'greater than or equal to') ||
    hasComparator(tokens, 'on or after') ||
    hasComparator(tokens, 'same or after') ||
    hasComparator(tokens, 'is at least') ||
    hasComparator(tokens, 'at least') ||
    hasComparator(tokens, 'no earlier than')
  ) {
    comparator = 'same-or-after';
  } else if (
    hasComparator(tokens, 'less than or equal to') ||
    hasComparator(tokens, 'on or before') ||
    hasComparator(tokens, 'same or before') ||
    hasComparator(tokens, 'at most') ||
    hasComparator(tokens, 'no later than')
  ) {
    comparator = 'same-or-before';
  } else if (
    hasComparator(tokens, 'greater than') ||
    hasComparator(tokens, 'more than') ||
    hasKeyword(tokens, 'after')
  ) {
    comparator = 'after';
  } else if (
    hasComparator(tokens, 'less than') ||
    hasKeyword(tokens, 'before')
  ) {
    comparator = 'before';
  } else if (
    hasKeyword(tokens, 'equals') ||
    hasKeyword(tokens, 'equal') ||
    hasKeyword(tokens, 'on')
  ) {
    comparator = 'equals';
  }

  return {
    intent: 'absolute-date-comparison',
    confidence: intent.confidence,
    tokens,
    field,
    comparator,
    dateISO,
  };
}

/**
 * Parse conditional pattern
 * "reason required unless waiver is checked" -> reason != null || waiver == true
 */
function parseConditional(
  tokens: NLToken[],
  input: string,
  intent: IntentMatch,
): NLConditionalPattern {
  if (hasKeyword(tokens, 'unless')) {
    const fields = extractFields(tokens);
    const [requiredField, exceptionField] = fields;
    if (requiredField === undefined || exceptionField === undefined)
      throw new Error('Conditional requires two fields');

    const isBoolean =
      hasKeyword(tokens, 'checked') || hasKeyword(tokens, 'unchecked');

    const conditionPattern: NLPresencePattern = {
      intent: 'presence',
      confidence: 0.9,
      tokens,
      field: requiredField,
      negated: false,
    };

    const consequentPattern: NLPattern = isBoolean
      ? ({
          intent: 'boolean',
          confidence: 0.9,
          tokens,
          field: exceptionField,
          expected: true,
        } as NLBooleanPattern)
      : ({
          intent: 'presence',
          confidence: 0.9,
          tokens,
          field: exceptionField,
          negated: false,
        } as NLPresencePattern);

    return {
      intent: 'conditional',
      confidence: intent.confidence,
      tokens,
      condition: conditionPattern,
      consequent: consequentPattern,
    };
  }

  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  if (fields.length < 2) {
    throw new Error(
      'Conditional requires at least two fields (condition and consequence)',
    );
  }

  const thenIndex = tokens.findIndex((t) => t.value.toLowerCase() === 'then');
  if (thenIndex === -1) {
    throw new Error('Conditional requires "then" keyword');
  }

  const conditionTokens = tokens.slice(0, thenIndex);
  const consequenceTokens = tokens.slice(thenIndex + 1);

  const conditionField = extractFields(conditionTokens)[0];

  if (!conditionField) {
    throw new Error('Could not identify condition field');
  }

  let conditionPattern: NLPattern;

  if (hasKeyword(conditionTokens, 'checked')) {
    conditionPattern = {
      intent: 'boolean',
      confidence: 0.9,
      tokens: conditionTokens,
      field: conditionField,
      expected: true,
    } as NLBooleanPattern;
  } else if (numbers[0] !== undefined) {
    const number = numbers[0];
    const inputLower = input.toLowerCase();

    let comparator: NLComparator = 'at-least';

    if (inputLower.includes('>=') || inputLower.includes('at least')) {
      comparator = 'at-least';
    } else if (
      inputLower.includes('>') ||
      inputLower.includes('greater than') ||
      inputLower.includes('more than')
    ) {
      comparator = 'greater-than';
    } else if (inputLower.includes('<=') || inputLower.includes('at most')) {
      comparator = 'at-most';
    } else if (inputLower.includes('<') || inputLower.includes('less than')) {
      comparator = 'less-than';
    } else {
      comparator = 'equals';
    }

    conditionPattern = {
      intent: 'comparison',
      confidence: 0.9,
      tokens: conditionTokens,
      field: conditionField,
      comparator,
      value: number,
    } as NLComparisonPattern;
  } else {
    conditionPattern = {
      intent: 'presence',
      confidence: 0.9,
      tokens: conditionTokens,
      field: conditionField,
      negated: false,
    } as NLPresencePattern;
  }

  const consequenceField = extractFields(consequenceTokens)[0];
  if (!consequenceField) {
    throw new Error('Could not identify consequence field');
  }

  const consequentPattern: NLPresencePattern = {
    intent: 'presence',
    confidence: 0.9,
    tokens: consequenceTokens,
    field: consequenceField,
    negated: false,
  };

  return {
    intent: 'conditional',
    confidence: intent.confidence,
    tokens,
    condition: conditionPattern,
    consequent: consequentPattern,
  };
}

/**
 * Parse field equality pattern
 */
function parseFieldEquality(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLFieldEqualityPattern {
  const fields = extractFields(tokens);
  const [field1, field2] = fields;
  if (field1 === undefined || field2 === undefined)
    throw new Error('Field equality requires two fields');

  let comparator: NLFieldEqualityPattern['comparator'] = 'equals';

  if (hasKeyword(tokens, 'equals') || hasKeyword(tokens, 'equal')) {
    comparator = 'equals';
  } else if (hasComparator(tokens, 'same as') || hasKeyword(tokens, 'same')) {
    comparator = 'same-as';
  } else if (hasKeyword(tokens, 'matches') || hasKeyword(tokens, 'match')) {
    comparator = 'matches';
  } else if (hasComparator(tokens, 'different from')) {
    comparator = 'not-equals';
  }

  return {
    intent: 'field-equality',
    confidence: intent.confidence,
    tokens,
    field1,
    field2,
    comparator,
  };
}

/**
 * Parse combined pattern - split on AND/OR connectors
 */
function parseCombined(
  tokens: NLToken[],
  _input: string,
  intent: IntentMatch,
): NLCombinedPattern {
  const connectors = findConnectors(tokens);

  const [connector] = connectors;
  if (connector === undefined) {
    throw new Error('No connector found in combined pattern');
  }
  const operator: NLBooleanOp = connector.type;

  const leftTokens = tokens.slice(0, connector.index);
  const rightTokens = tokens.slice(connector.index + 1);

  let leftPattern: NLPattern | null = null;
  let rightPattern: NLPattern | null = null;

  try {
    const leftStr = leftTokens.map((t) => t.value).join(' ');
    const leftResult = parse(leftTokens, leftStr);
    leftPattern = leftResult.pattern;
  } catch (_e) {
    leftPattern = createSimplePattern(leftTokens);
  }

  try {
    const rightStr = rightTokens.map((t) => t.value).join(' ');
    const rightResult = parse(rightTokens, rightStr);
    rightPattern = rightResult.pattern;
  } catch (_e) {
    rightPattern = createSimplePattern(rightTokens);
  }

  const patterns: NLPattern[] = [];
  if (leftPattern) patterns.push(leftPattern);
  if (rightPattern) patterns.push(rightPattern);

  return {
    intent: 'combined',
    confidence: intent.confidence,
    tokens,
    operator,
    patterns,
  };
}

/**
 * Create a simple pattern from tokens when parsing fails
 */
function createSimplePattern(tokens: NLToken[]): NLParsedPattern {
  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  const [fallbackField] = fields;
  if (hasKeyword(tokens, 'present') || hasKeyword(tokens, 'provided')) {
    if (fallbackField !== undefined) {
      const negated = hasKeyword(tokens, 'not');
      return {
        intent: 'presence',
        confidence: 0.7,
        tokens,
        field: fallbackField,
        negated,
      };
    }
  }

  if (hasKeyword(tokens, 'checked') || hasKeyword(tokens, 'unchecked')) {
    if (fallbackField !== undefined) {
      const expected =
        !hasKeyword(tokens, 'unchecked') && !hasKeyword(tokens, 'not');
      return {
        intent: 'boolean',
        confidence: 0.7,
        tokens,
        field: fallbackField,
        expected,
      };
    }
  }

  const [fallbackNumber] = numbers;
  if (fallbackNumber !== undefined && fallbackField !== undefined) {
    let comparator:
      | 'at-least'
      | 'greater-than'
      | 'less-than'
      | 'at-most'
      | 'equals'
      | 'not-equals' = 'at-least';

    const tokenStr = tokens
      .map((t) => t.value)
      .join(' ')
      .toLowerCase();
    const tokenWords = tokenStr.split(/\s+/);

    if (
      tokenStr.includes('greater than') ||
      tokenStr.includes('more than') ||
      tokenWords.includes('over')
    ) {
      comparator = 'greater-than';
    } else if (
      hasComparator(tokens, 'at least') ||
      hasComparator(tokens, 'greater than or equal')
    ) {
      comparator = 'at-least';
    } else if (
      hasComparator(tokens, 'at most') ||
      hasComparator(tokens, 'less than or equal')
    ) {
      comparator = 'at-most';
    } else if (hasComparator(tokens, 'less than')) {
      comparator = 'less-than';
    }

    return {
      intent: 'comparison',
      confidence: 0.7,
      tokens,
      field: fallbackField,
      comparator,
      value: fallbackNumber,
    };
  }

  return {
    intent: 'presence',
    confidence: 0.5,
    tokens,
    field: fields[0] || 'unknown',
    negated: false,
  };
}

/**
 * Generate expression from pattern
 */
export function generateExpression(pattern: NLParsedPattern): string {
  switch (pattern.intent) {
    case 'comparison':
      return generateComparisonExpr(pattern);
    case 'presence':
      return generatePresenceExpr(pattern);
    case 'range':
      return generateRangeExpr(pattern);
    case 'text-match':
      return generateTextMatchExpr(pattern);
    case 'length':
      return generateLengthExpr(pattern);
    case 'boolean':
      return generateBooleanExpr(pattern);
    case 'date-comparison':
      return generateDateComparisonExpr(pattern);
    case 'date-arithmetic':
      return generateDateArithmeticExpr(pattern);
    case 'absolute-date-comparison':
      return generateAbsoluteDateComparisonExpr(pattern);
    case 'field-equality':
      return generateFieldEqualityExpr(pattern);
    case 'combined':
      return generateCombinedExpr(pattern);
    case 'conditional':
      return generateConditionalExpr(pattern);
    default:
      return '';
  }
}

const EXPRESSION_RESERVED_IDENTIFIERS = new Set([
  'type',
  'let',
  'match',
  'true',
  'false',
  'null',
  'debugger',
  'and',
  'or',
  'not',
  'in',
  'if',
  'else',
  'then',
  'foreach',
  'when',
  'otherwise',
  'use',
  'return',
]);

function fieldRef(field: string): string {
  return EXPRESSION_RESERVED_IDENTIFIERS.has(field) ? `root.${field}` : field;
}

function generateComparisonExpr(p: NLComparisonPattern): string {
  const ops = {
    'at-least': '>=',
    'greater-than': '>',
    'less-than': '<',
    'at-most': '<=',
    equals: '==',
    'not-equals': '!=',
    between: 'between',
    outside: 'outside',
  };
  return `${fieldRef(p.field)} ${ops[p.comparator]} ${p.value}`;
}

function generatePresenceExpr(p: NLPresencePattern): string {
  const field = fieldRef(p.field);
  return p.negated
    ? `${field} == null || len(${field}) == 0`
    : `${field} != null`;
}

function generateRangeExpr(p: NLRangePattern): string {
  const field = fieldRef(p.field);
  const min = typeof p.min === 'string' ? fieldRef(p.min) : p.min;
  const max = typeof p.max === 'string' ? fieldRef(p.max) : p.max;
  if (p.negated) {
    return `${field} < ${min} || ${field} > ${max}`;
  }
  return `${field} >= ${min} && ${field} <= ${max}`;
}

function generateTextMatchExpr(p: NLTextMatchPattern): string {
  const field = fieldRef(p.field);
  const value = JSON.stringify(p.value ?? '');
  if (p.operation === 'has-text') {
    return `${field} != null && len(${field}) > 0`;
  }
  if (p.operation === 'contains') {
    const expression = `includes(${field}, ${value})`;
    return p.negated ? `!${expression}` : expression;
  }
  if (p.operation === 'starts-with') {
    const expression = `startsWith(${field}, ${value})`;
    return p.negated ? `!${expression}` : expression;
  }
  if (p.operation === 'ends-with') {
    const expression = `endsWith(${field}, ${value})`;
    return p.negated ? `!${expression}` : expression;
  }
  if (p.operation === 'matches') {
    if (!p.caseSensitive) {
      return `lower(${field}) == lower(${value})`;
    }
    return `${field} == ${value}`;
  }

  if (!p.caseSensitive) {
    return `lower(${field}) == lower(${value})`;
  }
  return `${field} == ${value}`;
}

function generateLengthExpr(p: NLLengthPattern): string {
  const operators = {
    'at-least': '>=',
    'greater-than': '>',
    'less-than': '<',
    'at-most': '<=',
    equals: '==',
  } as const;
  return `len(${fieldRef(p.field)}) ${operators[p.comparator]} ${p.length}`;
}

function generateBooleanExpr(p: NLBooleanPattern): string {
  const field = fieldRef(p.field);
  return p.expected
    ? `${field} == true`
    : `${field} == false || ${field} == null`;
}

function generateDateComparisonExpr(p: NLDateComparisonPattern): string {
  const ops = {
    after: '>',
    before: '<',
    'same-or-after': '>=',
    'same-or-before': '<=',
  };

  if (p.dateOnly) {
    return `gteDateOnly(${p.field1}, ${p.field2})`;
  }

  return `${fieldRef(p.field1)} ${ops[p.comparator]} ${fieldRef(p.field2)}`;
}

function generateDateArithmeticExpr(p: NLDateArithmeticPattern): string {
  if (p.unit === 'hours') {
    return `withinHours(${p.field1}, ${p.field2}, ${p.amount})`;
  }
  if (p.unit === 'minutes') {
    return `withinMinutes(${p.field1}, ${p.field2}, ${p.amount})`;
  }

  const field1 = p.field1 === 'now()' ? 'now()' : fieldRef(p.field1);
  const field2 = fieldRef(p.field2);

  if (p.comparator === 'within' || p.comparator === 'at-most') {
    return `diffDays(${field1}, ${field2}) <= ${p.amount}`;
  }
  if (p.comparator === 'exactly') {
    return `diffDays(${field1}, ${field2}) === ${p.amount}`;
  }

  return `diffDays(${field1}, ${field2}) >= ${p.amount}`;
}

function generateAbsoluteDateComparisonExpr(
  p: NLAbsoluteDateComparisonPattern,
): string {
  const ops = {
    after: '>',
    before: '<',
    'same-or-after': '>=',
    'same-or-before': '<=',
    equals: '==',
  } as const;
  return `${fieldRef(p.field)} ${ops[p.comparator]} ${p.dateISO}`;
}

function generateFieldEqualityExpr(p: NLFieldEqualityPattern): string {
  if (p.comparator === 'not-equals') {
    return `${fieldRef(p.field1)} != ${fieldRef(p.field2)}`;
  }

  return `${fieldRef(p.field1)} == ${fieldRef(p.field2)}`;
}

function generateCombinedExpr(p: NLCombinedPattern): string {
  const expressions = p.patterns.map((pattern) =>
    generateExpression(pattern as NLParsedPattern),
  );
  const op = p.operator === 'and' ? '&&' : '||';
  return expressions.join(` ${op} `);
}

function generateConditionalExpr(p: NLConditionalPattern): string {
  const conditionExpr = generateExpression(p.condition as NLParsedPattern);
  const consequentExpr = generateExpression(p.consequent as NLParsedPattern);

  const isUnlessPattern =
    p.condition.intent === 'presence' &&
    !(p.condition as NLPresencePattern).negated &&
    ((p.consequent.intent === 'presence' &&
      !(p.consequent as NLPresencePattern).negated) ||
      (p.consequent.intent === 'boolean' &&
        (p.consequent as NLBooleanPattern).expected));

  if (isUnlessPattern) {
    return `${conditionExpr} || ${consequentExpr}`;
  }

  const negatedCondition = negateExpression(conditionExpr);
  return `${negatedCondition} || ${consequentExpr}`;
}

function negateExpression(expr: string): string {
  if (expr.includes(' >= ')) return expr.replace(' >= ', ' < ');
  if (expr.includes(' > ')) return expr.replace(' > ', ' <= ');
  if (expr.includes(' <= ')) return expr.replace(' <= ', ' > ');
  if (expr.includes(' < ')) return expr.replace(' < ', ' >= ');
  if (expr.includes(' == true')) return expr.replace(' == true', ' != true');
  if (expr.includes(' != null')) return expr.replace(' != null', ' == null');
  if (expr.includes(' == null')) return expr.replace(' == null', ' != null');
  if (expr.includes(' == ')) return expr.replace(' == ', ' != ');
  if (expr.includes(' != ')) return expr.replace(' != ', ' == ');

  return `!(${expr})`;
}

/**
 * Describe intent for user
 */
function describeIntent(intent: IntentMatch): string {
  const descriptions: Record<string, string> = {
    comparison: 'Comparing a field to a value',
    presence: 'Checking if a field has a value',
    range: 'Checking if a value is within a range',
    'text-match': 'Matching text content',
    boolean: 'Checking a checkbox state',
    'date-comparison': 'Comparing two dates',
    'date-arithmetic': 'Calculating date differences',
    'absolute-date-comparison': 'Comparing a date field to a specific date',
    conditional: 'Conditional requirement',
    'field-equality': 'Comparing two fields',
    combined: 'Multiple conditions combined',
  };
  return descriptions[intent.intent] || intent.intent;
}
